import { getApiConfigNext, MatchFromJSON, type Match } from '@y-sports/api-client';
import { AdminApi } from '@y-sports/api-client/generated-next-management/src/apis/AdminApi';
import type { AdminPlayerDetail, AdminPlayerStats, Training } from '@y-sports/api-client/generated-next-management/src/models';
import { AdminPlayerDetailFromJSON } from '@y-sports/api-client/generated-next-management/src/models/AdminPlayerDetail';
import { Configuration as NextManagementConfiguration } from '@y-sports/api-client/generated-next-management/src/runtime';
import type { TeamBasic, TeamRegistrationTemplate, TournamentSummary } from '@y-sports/api-client/generated-v2/src/models';
import { TeamDataService } from './TeamDataService';
import { TrainingDataService } from './TrainingDataService';
import { TournamentOverviewDataService } from './TournamentOverviewDataService';
import { TournamentScheduleDataService } from './TournamentScheduleDataService';

export type AdminManagedMatch = {
  matchId: string;
  createdBy?: string | null;
  tournamentId?: string | null;
  isAdHoc?: boolean;
  tournamentName?: string | null;
  tournamentDate?: string | null;
  categoryName?: string | null;
  matchTime?: string | null;
  stageName?: string | null;
  groupName?: string | null;
  label?: string | null;
  courtName?: string | null;
  teamAName?: string | null;
  teamBName?: string | null;
  teamAScore?: number | null;
  teamBScore?: number | null;
  status?: string | null;
};

export type AdminAccountAsset = {
  id?: string | null;
  name?: string | null;
  type?: string | null;
  role?: string | null;
};

export type AdminAccountUser = {
  email: string;
  subject?: string | null;
  displayName?: string | null;
  assignedRoles: string[];
  effectiveRoles: string[];
  assets: AdminAccountAsset[];
};

export type AdminPlayerDetailWithOwnership = AdminPlayerDetail & {
  connectedUserEmail?: string | null;
  connectedUserSubject?: string | null;
  playerInviteEmail?: string | null;
};

const getAdminApi = async () => {
  const apiConfig = await getApiConfigNext();
  return new AdminApi(new NextManagementConfiguration({
    basePath: apiConfig.basePath,
    headers: apiConfig.headers,
  }));
};

const createJsonHeaders = (headers?: HeadersInit): Headers => {
  const normalizedHeaders = new Headers(headers);
  if (!normalizedHeaders.has('Accept') && !normalizedHeaders.has('accept')) {
    normalizedHeaders.set('Accept', 'application/json');
  }
  return normalizedHeaders;
};

export const AdminDataService = {
  getAdminPlayers: async (): Promise<AdminPlayerStats[]> => {
    const adminApi = await getAdminApi();
    return adminApi.listAdminPlayerStats();
  },

  getAdminTrainings: async (): Promise<Training[]> => {
    return TrainingDataService.getTrainings();
  },

  getAdminTournaments: async (): Promise<TournamentSummary[]> => {
    const tournamentResponse = await TournamentOverviewDataService.getTournamentsList();
    const allTournaments: TournamentSummary[] = tournamentResponse.tournaments || [];

    return allTournaments;
  },

  getAdminTeams: async (): Promise<TeamBasic[]> => {
    return TeamDataService.getAllTeamsSummary();
  },

  getAdminTemplates: async (): Promise<TeamRegistrationTemplate[]> => {
    return (await TeamDataService.getAdminTeamRegistrationTemplates(true)) || [];
  },

  getAdminMatches: async (): Promise<AdminManagedMatch[]> => {
    const [tournaments, adHocMatches, creators] = await Promise.all([
      AdminDataService.getAdminTournaments(),
      AdminDataService.getAdminAdHocMatches(),
      AdminDataService.getAdminMatchCreators(),
    ]);
    const creatorByMatchId = new Map(creators.map(creator => [creator.matchId, creator.createdBy]));
    const scheduleResponses = await Promise.all(
      tournaments
        .filter((tournament) => Boolean(tournament.id))
        .map(async (tournament) => {
          const summary = await TournamentScheduleDataService.getTournamentScheduleSummary(tournament.id!);

          return (summary.matches || []).map((match) => ({
            matchId: match.id,
            createdBy: creatorByMatchId.get(match.id) || null,
            tournamentId: tournament.id!,
            tournamentName: tournament.name || null,
            tournamentDate: tournament.date ? String(tournament.date) : null,
            categoryName: match.category?.name || null,
            matchTime: match.matchTime || null,
            stageName: match.stageName || null,
            groupName: match.groupName || null,
            label: match.label || null,
            courtName: match.court?.name || null,
            teamAName: match.teamA?.name || null,
            teamBName: match.teamB?.name || null,
            teamAScore: match.teamAScore ?? null,
            teamBScore: match.teamBScore ?? null,
            status: match.status || null,
          } satisfies AdminManagedMatch));
        }),
    );
    const mappedAdHocMatches = adHocMatches.map((match) => ({
      matchId: match.id || '',
      createdBy: creatorByMatchId.get(match.id || '') || null,
      tournamentId: null,
      isAdHoc: true,
      tournamentName: 'Ad-hoc matches',
      tournamentDate: null,
      categoryName: 'Ad-hoc',
      matchTime: match.matchTime ? match.matchTime.toISOString() : null,
      stageName: null,
      groupName: match.group || null,
      label: match.label || null,
      courtName: match.court?.name || null,
      teamAName: match.teamA?.name || null,
      teamBName: match.teamB?.name || null,
      teamAScore: match.teamAScore ?? null,
      teamBScore: match.teamBScore ?? null,
      status: match.status || null,
    } satisfies AdminManagedMatch));

    return [
      ...mappedAdHocMatches,
      ...scheduleResponses.flat(),
    ]
      .filter((match, index, items) => items.findIndex((candidate) => candidate.matchId === match.matchId) === index);
  },

  getAdminAccountUsers: async (): Promise<AdminAccountUser[]> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/admin/account-access`, {
      headers: createJsonHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to load account access: ${response.status}`);
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload : [];
  },

  updateAdminAccountRoles: async (email: string, roles: string[]): Promise<AdminAccountUser> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/admin/account-access/${encodeURIComponent(email)}/roles`, {
      method: 'PATCH',
      headers: createJsonHeaders({
        ...(apiConfig.headers || {}),
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ roles }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update account roles: ${response.status}`);
    }

    return response.json();
  },

  getAdminMatchCreators: async (): Promise<Array<{ matchId: string; createdBy?: string | null }>> => {
    const api = await getAdminApi();
    return api.getAdminMatchCreators();
  },

  getAdminAdHocMatches: async (): Promise<Match[]> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/matches/ad-hoc/admin`, {
      headers: createJsonHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to load ad-hoc matches: ${response.status}`);
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload.map(MatchFromJSON) : [];
  },

  getAdminPlayerDetail: async (playerId: string): Promise<AdminPlayerDetailWithOwnership> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/admin/player-stats/${encodeURIComponent(playerId)}`, {
      headers: createJsonHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch admin player detail (${response.status})`);
    }

    const rawDetail = await response.json();
    const parsedDetail = AdminPlayerDetailFromJSON(rawDetail);

    return {
      ...parsedDetail,
      connectedUserEmail: rawDetail?.connectedUserEmail ?? null,
      connectedUserSubject: rawDetail?.connectedUserSubject ?? null,
      playerInviteEmail: rawDetail?.playerInviteEmail ?? rawDetail?.email ?? null,
      trainings: parsedDetail.trainings.map((record, index) => ({
        ...record,
        late: rawDetail?.trainings?.[index]?.late,
        sessionId: rawDetail?.trainings?.[index]?.sessionId,
      })),
    } as AdminPlayerDetailWithOwnership;
  },

  updateAdminPlayerInviteEmail: async (playerId: string, email: string): Promise<AdminPlayerDetailWithOwnership> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/admin/player-stats/${encodeURIComponent(playerId)}/invite`, {
      method: 'PATCH',
      headers: createJsonHeaders({
        ...(apiConfig.headers || {}),
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update player invite email (${response.status})`);
    }

    const rawDetail = await response.json();
    const parsedDetail = AdminPlayerDetailFromJSON(rawDetail);
    return {
      ...parsedDetail,
      connectedUserEmail: rawDetail?.connectedUserEmail ?? null,
      connectedUserSubject: rawDetail?.connectedUserSubject ?? null,
      playerInviteEmail: rawDetail?.playerInviteEmail ?? rawDetail?.email ?? null,
      trainings: parsedDetail.trainings.map((record, index) => ({
        ...record,
        late: rawDetail?.trainings?.[index]?.late,
        sessionId: rawDetail?.trainings?.[index]?.sessionId,
      })),
    } as AdminPlayerDetailWithOwnership;
  },

  claimPlayerProfileInvitations: async (email: string): Promise<AdminPlayerDetailWithOwnership[]> => {
    const apiConfig = await getApiConfigNext();
    const normalizedEmail = email.trim().toLowerCase();
    const response = await fetch(`${apiConfig.basePath}/users/${encodeURIComponent(normalizedEmail)}/player-profile-claims`, {
      method: 'POST',
      headers: createJsonHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to claim player profile invitations (${response.status})`);
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload : [];
  },

  deleteTeam: async (teamId: string): Promise<void> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}`, {
      method: 'DELETE',
      headers: {
        ...(apiConfig.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Delete failed with status ${response.status}`);
    }
  },

  deleteTournament: async (tournamentId: string): Promise<void> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}`, {
      method: 'DELETE',
      headers: {
        ...(apiConfig.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Delete failed with status ${response.status}`);
    }
  },

  deleteAdHocMatch: async (matchId: string): Promise<void> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/matches/ad-hoc/${matchId}`, {
      method: 'DELETE',
      headers: {
        ...(apiConfig.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Delete failed with status ${response.status}`);
    }
  },
};
